// Newest-first paging over PC.Forecast_Audit_Log for one or more Forecast_Tab
// values, used by the per-tab audit history panels.
//
// Why this exists: Labor and Equipment are each written under two Forecast_Tab
// values. The deployed readAuditLog can only filter on a single scalar `tab`, so
// asking for both at once returned every tab's rows, and the panel filtered them
// in the browser. On jobs whose newest audit rows belong to other tabs, a whole
// page filtered down to nothing — the panel said "No recorded changes" while
// hundreds existed, and "Load more" re-requested that same page forever, because
// its cursor was the last *shown* row and nothing had been shown.
//
// Instead, each value is its own server-filtered stream, and the streams are
// merged by Audit_ID. Single-value tabs are simply one stream. This works against
// readAuditLog whether or not the multi-value `tabs` parameter is deployed.

// Audit_ID is a SQL BIGINT, which the API returns as a string. Compared as
// strings "9" sorts above "10", which would scramble the merge — always compare
// numerically.
const auditId = (row) => Number(row.Audit_ID);

export const initialAuditStreams = (tabValues) =>
    tabValues.map(tab => ({ tab, buffer: [], cursor: null, exhausted: false }));

/**
 * Loads up to `pageSize` more rows, newest first, advancing `streams` in place.
 *
 * `request(tab, beforeId)` must resolve to that tab's rows, newest first, capped
 * at `pageSize`, and throw on failure.
 *
 * Resolves to `{ rows, hasMore, error }`. On a failed request it still returns
 * the rows it had already merged, with `error` set, so a failed "Load more" never
 * silently drops rows it had already taken out of the streams.
 */
export async function loadNextAuditPage({ streams, pageSize, request }) {
    const rows = [];
    while (rows.length < pageSize) {
        // Refill every stream that could still produce rows but has none buffered.
        // Merging is only safe once each live stream has a row to compare —
        // otherwise a row not yet fetched could be newer than one about to be shown.
        for (const s of streams) {
            if (s.exhausted || s.buffer.length > 0) continue;
            let page;
            try {
                page = await request(s.tab, s.cursor);
            } catch (error) {
                return { rows, hasMore: true, error };
            }
            // The cursor is the last row the *server* returned for this stream, so
            // it always advances, even across rows that are later discarded.
            if (page.length > 0) s.cursor = page[page.length - 1].Audit_ID;
            s.buffer.push(...page);
            if (page.length < pageSize) s.exhausted = true;
        }

        if (!streams.some(s => s.buffer.length > 0)) break;

        // Emit newest-first until a live stream runs dry, then loop to refill it.
        while (rows.length < pageSize) {
            if (streams.some(s => !s.exhausted && s.buffer.length === 0)) break;
            let newest = null;
            for (const s of streams) {
                if (s.buffer.length === 0) continue;
                if (!newest || auditId(s.buffer[0]) > auditId(newest.buffer[0])) newest = s;
            }
            if (!newest) break;
            rows.push(newest.buffer.shift());
        }
    }
    return { rows, hasMore: streams.some(s => s.buffer.length > 0 || !s.exhausted), error: null };
}
